import { useState } from "react";
import type { ScheduledDelivery, ScheduledDeliveryLine } from "../../api/types";
import { findDeliveryLine } from "../../utils/findDeliveryLine";
import { BarcodeField } from "../BarcodeField";
import { useT } from "../../i18n/LanguageProvider";
import { timeText } from "../../i18n/intl";

/**
 * G-59 — the scan box at the top of both receiving screens. Scan a box (or
 * type the supplier code) and the matching PENDING line is handed to the
 * parent, which puts it first and highlights it. Every outcome gets a
 * sentence: not on this delivery; already confirmed (by whom, when); two
 * pending lines for one code (pick one).
 */
interface Props {
  delivery: ScheduledDelivery;
  disabled?: boolean;
  onFound: (line: ScheduledDeliveryLine, how: "SCANNED" | "TYPED") => void;
  /**
   * A code that matched nothing. When it looks like a barcode the item has
   * simply not learned yet, the parent prefills "Add barcode from the box"
   * with it so the florist does not scan the same box twice.
   */
  onUnmatched?: (code: string) => void;
}

/** S5: 24-hour, and in the florist's language rather than the device's. */
const fmtTime = (iso: string | null) => (iso ? timeText(new Date(iso)) : "");

export function DeliveryScanBox({ delivery, disabled, onFound, onUnmatched }: Props) {
  const { t } = useT();
  const [code, setCode] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [choices, setChoices] = useState<ScheduledDeliveryLine[]>([]);
  const [how, setHow] = useState<"SCANNED" | "TYPED">("TYPED");

  const lineNumber = (line: ScheduledDeliveryLine) =>
    delivery.lines.findIndex((l) => l.id === line.id) + 1;

  const lookUp = (value: string, via: "SCANNED" | "TYPED") => {
    setChoices([]);
    const match = findDeliveryLine(delivery, value);
    if (match.kind === "none") {
      const looksLikeBarcode = value.replace(/\D/g, "").length >= 8;
      setMessage(
        looksLikeBarcode
          ? t("receiving.notOnDeliveryYet", { code: value.trim() })
          : t("receiving.notOnDelivery", { code: value.trim() }),
      );
      if (looksLikeBarcode) onUnmatched?.(value.trim());
      return;
    }
    if (match.kind === "confirmed") {
      const who = match.line.confirmedBy?.fullName ?? t("receiving.someone");
      setMessage(
        t("receiving.alreadyConfirmed", {
          number: lineNumber(match.line),
          item: match.line.item.name,
          who,
          time: fmtTime(match.line.confirmedAt),
        }),
      );
      return;
    }
    if (match.kind === "several") {
      setHow(via);
      setChoices(match.lines);
      setMessage(
        t("receiving.severalPending", {
          count: match.lines.length,
          code: value.trim(),
        }),
      );
      return;
    }
    setMessage(
      t("receiving.foundLine", {
        number: lineNumber(match.line),
        item: match.line.item.name,
      }),
    );
    setCode("");
    onFound(match.line, via);
  };

  return (
    <div className="delivery-scan-box">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) lookUp(code, "TYPED");
        }}
      >
        <BarcodeField
          id="delivery-scan-code"
          label={t("receiving.scanBoxLabel")}
          value={code}
          onChange={setCode}
          onScanDetected={(scanned) => lookUp(scanned, "SCANNED")}
          hint={
            disabled ? t("receiving.deliveryClosed") : t("receiving.scanHint")
          }
          // On the input row, beside Scan — not a sibling of the whole field,
          // which dropped it a line below (owner 2026-09-11).
          actions={
            <button
              type="submit"
              className="button plain"
              disabled={disabled || !code.trim()}
            >
              {t("receiving.findLine")}
            </button>
          }
        />
      </form>
      {message ? (
        <p className="delivery-scan-box__message" role="status">
          {message}
        </p>
      ) : null}
      {choices.length > 0 ? (
        <ul className="delivery-scan-box__choices">
          {choices.map((line) => (
            <li key={line.id}>
              <button
                type="button"
                className="button tinted"
                onClick={() => {
                  setChoices([]);
                  setMessage(
                    t("receiving.foundLine", {
                      number: lineNumber(line),
                      item: line.item.name,
                    }),
                  );
                  setCode("");
                  onFound(line, how);
                }}
              >
                {t("receiving.choiceLine", {
                  number: lineNumber(line),
                  item: line.item.name,
                  zone: line.zone.name,
                  qty: Number(line.expectedQty),
                })}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
